import React, { useMemo } from 'react';
import { useJobs } from 'src/hooks/useJobs';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { IconFileText, IconPlus } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { SubmittalDto } from 'src/api/FiretrolApiClient';
import { useSubmittalsList } from 'src/api/queries/submittals/useSubmittalQueries';
import { useSubmittalLogPdf } from 'src/api/queries/submittals/useSubmittalMutations';
import { IContact } from 'src/api/models/Organization';
import {
  getErrorMessage,
  logger,
  resolveWorkflowButtonColor,
  showErrorToast,
} from 'src/utils';
import { WorkflowCreateButton } from 'src/app/workflows/WorkflowCreateButton';
import { workflowStatusColumn } from 'src/app/workflows/workflowStatusColumn';
import { WorkflowStatusPanel } from 'src/design/molecules/WorkflowStatusPanel';
import { WorkflowLogModal } from 'src/app/workflows/WorkflowLogModal';
import { useWorkflowStatusFilters } from 'src/app/workflows/useWorkflowStatusFilters';
import { useWorkflowStatusSegments } from 'src/app/workflows/useWorkflowStatusSegments';
import { useWorkflowLogPdf } from 'src/app/workflows/useWorkflowLogPdf';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { usePdfBlobUrl } from 'src/design/molecules/usePdfBlobUrl';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';

const intentLabel = (intent: string | undefined): string =>
  intent === 'info_only' ? 'Information Only' : 'Review and Acceptance';

const getColumns = (): MRT_ColumnDef<SubmittalDto>[] => [
  {
    accessorKey: 'submittalNumber',
    header: 'Submittal #',
    size: 150,
  },
  {
    accessorKey: 'title',
    header: 'Title',
    size: 220,
  },
  workflowStatusColumn<SubmittalDto>({ size: 160 }),
  {
    accessorKey: 'type',
    header: 'Type',
    size: 140,
  },
  {
    id: 'intent',
    header: 'Intent',
    size: 200,
    accessorFn: (row) => intentLabel(row.intentOfReview),
  },
  {
    id: 'submittedTo',
    header: 'Submitted To',
    size: 200,
    accessorFn: (row) => row.submittedToContact?.name || '',
  },
  {
    id: 'assignee',
    header: 'Assignee',
    size: 180,
    accessorFn: (row) => row.assignee?.fullName || '',
  },
  {
    id: 'daysOpen',
    header: 'Days Open',
    size: 120,
    accessorFn: (row) => (row.daysOpen != null ? String(row.daysOpen) : ''),
  },
];

export const Submittals: React.FC = () => {
  const { selectedJob } = useJobs();
  const { data, isLoading, isFetching, isError, refetch } = useSubmittalsList(
    selectedJob?.id,
  );
  const submittals = React.useMemo<SubmittalDto[]>(() => data ?? [], [data]);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    covered,
    filters,
    selectableStatuses,
    statusFilter,
    setStatusFilter,
    filteredItems,
  } = useWorkflowStatusFilters(submittals, selectedJob?.submittalWorkflow);
  const segments = useWorkflowStatusSegments(
    submittals,
    filters,
    covered,
    selectedJob?.submittalWorkflow,
  );

  const [logOpen, setLogOpen] = React.useState(false);
  const [logStatuses, setLogStatuses] = React.useState<string[]>([]);
  const [logContact, setLogContact] = React.useState<IContact | null>(null);
  const logPdf = useSubmittalLogPdf();
  const { file: logFile, loading: logLoading } = useWorkflowLogPdf({
    open: logOpen,
    jobId: selectedJob?.id,
    statuses: logStatuses,
    contact: logContact,
    fetchPdf: (jobId, statuses, contactId) =>
      logPdf.mutateAsync({ jobId, statuses, contactId }),
    logLabel: 'Failed to load submittal log PDF',
    onError: () => setLogOpen(false),
  });
  const logUrl = usePdfBlobUrl(logFile?.data);

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'job-submittals',
    columns: columns,
    defaultSorting: [{ id: 'submittalNumber', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => (
        <Group>
          <ViewActionIcon
            onClick={() =>
              navigate(
                `/jobs/${row.original.jobId}/submittals/${row.original.id}`,
              )
            }
            label="View Submittal"
          />
        </Group>
      ),
      enableClickToCopy: false,
      columns: columns,
      data: filteredItems,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: filteredItems.length,
    isLoading,
    isFetching,
    isError,
    onRefresh: () => {
      refetch();
    },
  });

  if (!selectedJob) return null;

  return (
    <>
      <Header
        breadcrumbProps={{
          items: jobBreadcrumbs(selectedJob, undefined, 'Submittals'),
        }}
        title="Submittals"
        helpText="Manage submittals for this job. Create new submittals, track their review status, and manage document revisions."
        actions={
          selectedJob.submittalWorkflow ? (
            <WorkflowCreateButton
              workflow={selectedJob.submittalWorkflow}
              color={resolveWorkflowButtonColor(
                selectedJob.submittalWorkflow.createButtonColor,
              )}
              leftSection={<IconPlus />}
              onClick={() => navigate(`${location.pathname}/submit/submittal`)}
            >
              {selectedJob.submittalWorkflow.createButtonText ||
                'Create Submittal'}
            </WorkflowCreateButton>
          ) : undefined
        }
      />

      <Group mb="md">
        <SecondaryButton
          leftSection={<IconFileText />}
          onClick={() => setLogOpen(true)}
        >
          Submittal Log
        </SecondaryButton>
      </Group>

      {!selectedJob.submittalWorkflowId && (
        <Alert mb="md" title="Missing Submittal Workflow" color="red">
          Default workflow does not exist. Please contact an administrator.
        </Alert>
      )}

      <WorkflowStatusPanel
        segments={segments}
        value={statusFilter}
        onChange={setStatusFilter}
      />

      {table}

      <WorkflowLogModal
        opened={logOpen}
        onClose={() => setLogOpen(false)}
        title="Submittal Log"
        pdfTitle="Submittal Log PDF"
        statusOptions={selectableStatuses}
        statuses={logStatuses}
        onStatusChange={setLogStatuses}
        customerNumber={selectedJob.customerNumber?.trim() ?? ''}
        contact={logContact}
        onContactChange={setLogContact}
        logUrl={logUrl}
        logLoading={logLoading}
      />
    </>
  );
};
